/**
 * Marketplace Search Page - Todo en Local
 * Search results with filters, map view, and product grid
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Search, MapPin, Filter, X, ChevronDown, Star, Heart, 
  Package, Store, Truck, Shield, Grid, Map as MapIcon,
  SlidersHorizontal, Check, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import { getProductImage } from '../../utils/constants';
import api from '../../services/api';
import { API_URL } from '../../config';
import SearchAutocomplete from '../../components/marketplace/SearchAutocomplete';
import { VerifiedStoreName } from '../../components/marketplace/VerifiedBadge';
import { MarketplaceHeader, MarketplaceFooter, FloatingCartButton, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';

// Import Leaflet for maps
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Brand colors - Professional & Modern
const COLORS = {
  navyBlue: '#1e3a5f',      // Azul marino profundo
  darkSlate: '#0f172a',     // Slate muy oscuro
  orange: '#F37322',        // Naranja - CTA principal
  lightGray: '#f8fafc',
  white: '#ffffff'
};

// Custom marker icon
const storeIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map center updater component
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
};

const MarketplaceSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'grid'); // grid or map
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  
  const [userLocation, setUserLocation] = useState(null);
  const [locationAttempted, setLocationAttempted] = useState(false);
  const [mapCenter, setMapCenter] = useState([19.4326, -99.1332]); // Mexico City default
  
  // Cart state for floating button
  const [cartInfo, setCartInfo] = useState({ itemCount: 0, total: 0 });
  
  // Favorites state
  const [favorites, setFavorites] = useState(new Set());
  
  // Filters state
  const [filters, setFilters] = useState({
    master_category_id: searchParams.get('master_category') || '',
    category_id: searchParams.get('category') || '',
    delivery_enabled: searchParams.get('delivery') === 'true',
    pickup_enabled: searchParams.get('pickup') === 'true',
    shipping_enabled: searchParams.get('shipping') === 'true',
    wholesale_enabled: searchParams.get('wholesale') === 'true',
    verified_only: searchParams.get('verified') === 'true',
    open_now: searchParams.get('open_now') === 'true',  // New filter
    business_type: searchParams.get('type') || '',
    condition: searchParams.get('condition') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    radius_km: searchParams.get('radius') || '50',
    sort_by: searchParams.get('sort') || 'relevance'
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    loadCategories();
    loadMasterCategories();
    loadBusinessTypes();
    getUserLocation();
    loadCartInfo();
    loadFavorites();
  }, []);

  // Only perform search after location attempt is complete
  useEffect(() => {
    if (locationAttempted) {
      performSearch();
    }
  }, [searchParams, userLocation, locationAttempted, currentPage]);

  // Helper to check if a branch is currently open
  const isBranchOpen = (branch) => {
    if (!branch) return true;
    if (branch.is_manually_closed) return false;
    if (!branch.is_active) return false;
    
    const hours = branch.business_hours;
    if (!hours) return true; // No hours = always open
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.toTimeString().slice(0, 5);
    
    const schedule = hours[currentDay];
    if (!schedule || !schedule.is_open) return false;
    
    const openTime = schedule.open_time || '09:00';
    const closeTime = schedule.close_time || '18:00';
    
    return currentTime >= openTime && currentTime < closeTime;
  };

  // Get open status message
  const getOpenStatusMessage = (branch) => {
    if (!branch) return null;
    if (branch.is_manually_closed) return { open: false, msg: 'Cerrado' };
    
    const hours = branch.business_hours;
    if (!hours) return { open: true, msg: 'Abierto' };
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.toTimeString().slice(0, 5);
    
    const schedule = hours[currentDay];
    if (!schedule || !schedule.is_open) return { open: false, msg: 'Cerrado hoy' };
    
    const openTime = schedule.open_time || '09:00';
    const closeTime = schedule.close_time || '18:00';
    
    if (currentTime < openTime) return { open: false, msg: `Abre ${openTime}` };
    if (currentTime >= closeTime) return { open: false, msg: 'Cerrado' };
    
    // Check closing soon
    const [ch, cm] = closeTime.split(':').map(Number);
    const [nh, nm] = currentTime.split(':').map(Number);
    const minsLeft = (ch * 60 + cm) - (nh * 60 + nm);
    if (minsLeft <= 30) return { open: true, msg: `Cierra ${minsLeft}m` };
    
    return { open: true, msg: 'Abierto' };
  };

  const loadFavorites = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) return;
    
    try {
      const response = await api.get(`/marketplace/favorites?customer_token=${customerToken}`);
      const favIds = new Set(response.data.map(f => f.product_id));
      setFavorites(favIds);
    } catch (error) {
      // Silently fail
    }
  };

  const toggleFavorite = async (e, productId, businessId = null, branchId = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) {
      toast.info('Inicia sesión para guardar favoritos');
      navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }

    try {
      if (favorites.has(productId)) {
        // Remove from favorites
        const favResponse = await api.get(`/marketplace/favorites?customer_token=${customerToken}`);
        const favToRemove = favResponse.data.find(f => {
          const favProductId = f.product?.id || f.product_id;
          return favProductId === productId;
        });
        if (favToRemove) {
          await api.delete(`/marketplace/favorites/${favToRemove.favorite_id}?customer_token=${customerToken}`);
          setFavorites(prev => {
            const next = new Set(prev);
            next.delete(productId);
            return next;
          });
          toast.success('Eliminado de favoritos');
        }
      } else {
        // Add to favorites with business and branch info
        await api.post(`/marketplace/favorites?customer_token=${customerToken}`, {
          product_id: productId,
          business_id: businessId,
          branch_id: branchId
        });
        setFavorites(prev => new Set([...prev, productId]));
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const loadCartInfo = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) return;
    
    try {
      const response = await api.get(`/marketplace/cart?customer_token=${customerToken}`);
      setCartInfo({
        itemCount: response.data.items?.length || 0,
        total: response.data.total || 0
      });
    } catch (error) {
      // Silently fail - cart info is optional
    }
  };

  const getUserLocation = () => {
    // First, try to get cached location from localStorage
    const cachedLocation = localStorage.getItem('userLocation');
    if (cachedLocation) {
      try {
        const loc = JSON.parse(cachedLocation);
        const cacheTime = localStorage.getItem('userLocationTime');
        // Use cached location if less than 5 minutes old
        if (cacheTime && Date.now() - parseInt(cacheTime) < 300000) {
          setUserLocation(loc);
          setMapCenter([loc.lat, loc.lng]);
          setLocationAttempted(true);
          return;
        }
      } catch (e) {
        console.log('Error parsing cached location');
      }
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setMapCenter([loc.lat, loc.lng]);
          setLocationAttempted(true);
          // Cache the location
          localStorage.setItem('userLocation', JSON.stringify(loc));
          localStorage.setItem('userLocationTime', Date.now().toString());
        },
        (error) => {
          console.log('Location not available');
          setLocationAttempted(true); // Still mark as attempted even if failed
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    } else {
      setLocationAttempted(true); // No geolocation support
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/marketplace/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadMasterCategories = async () => {
    try {
      const response = await api.get('/marketplace/master-categories');
      setMasterCategories(response.data);
    } catch (error) {
      console.error('Error loading master categories:', error);
    }
  };

  const loadBusinessTypes = async () => {
    try {
      const response = await api.get('/marketplace/business-types');
      setBusinessTypes(response.data);
    } catch (error) {
      console.error('Error loading business types:', error);
    }
  };

  const performSearch = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      
      const q = searchParams.get('q');
      if (q) params.append('q', q);
      
      if (filters.master_category_id) params.append('master_category_id', filters.master_category_id);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.delivery_enabled) params.append('delivery_enabled', 'true');
      if (filters.pickup_enabled) params.append('pickup_enabled', 'true');
      if (filters.shipping_enabled) params.append('shipping_enabled', 'true');
      if (filters.wholesale_enabled) params.append('wholesale_enabled', 'true');
      if (filters.verified_only) params.append('verified_only', 'true');
      if (filters.business_type) params.append('business_type', filters.business_type);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.radius_km) params.append('radius_km', filters.radius_km);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      
      params.append('page', currentPage);
      params.append('limit', 20);
      
      const response = await api.get(`/marketplace/search?${params.toString()}`);
      
      setResults(response.data.products || []);
      setTotalResults(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
      
    } catch (error) {
      console.error('Error searching:', error);
      // Only show toast for actual server errors, not for empty results
      if (error.response?.status >= 500) {
        toast.error('Error al buscar productos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery.trim()) {
      newParams.set('q', searchQuery);
    } else {
      newParams.delete('q');
    }
    setSearchParams(newParams);
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams();
    
    if (searchQuery) newParams.set('q', searchQuery);
    if (filters.master_category_id) newParams.set('master_category', filters.master_category_id);
    if (filters.category_id) newParams.set('category', filters.category_id);
    if (filters.delivery_enabled) newParams.set('delivery', 'true');
    if (filters.pickup_enabled) newParams.set('pickup', 'true');
    if (filters.shipping_enabled) newParams.set('shipping', 'true');
    if (filters.wholesale_enabled) newParams.set('wholesale', 'true');
    if (filters.verified_only) newParams.set('verified', 'true');
    if (filters.business_type) newParams.set('type', filters.business_type);
    if (filters.condition) newParams.set('condition', filters.condition);
    if (filters.min_price) newParams.set('min_price', filters.min_price);
    if (filters.max_price) newParams.set('max_price', filters.max_price);
    if (filters.radius_km !== '50') newParams.set('radius', filters.radius_km);
    if (filters.sort_by !== 'relevance') newParams.set('sort', filters.sort_by);
    if (viewMode !== 'grid') newParams.set('view', viewMode);
    
    setSearchParams(newParams);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      master_category_id: '',
      category_id: '',
      delivery_enabled: false,
      pickup_enabled: false,
      shipping_enabled: false,
      wholesale_enabled: false,
      verified_only: false,
      open_now: false,
      business_type: '',
      condition: '',
      min_price: '',
      max_price: '',
      radius_km: '50',
      sort_by: 'relevance'
    });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v && v !== '' && v !== '50' && v !== 'relevance').length;

  // Get map markers from results
  const mapMarkers = results
    .filter(r => r.branch?.location?.lat && r.branch?.location?.lng)
    .map(r => ({
      id: r.product.id,
      position: [r.branch.location.lat, r.branch.location.lng],
      product: r.product,
      store: r.store,
      branch: r.branch
    }));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <MarketplaceHeader />

      {/* Search + Filter Bar */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 py-2 space-y-2">
          {/* Mobile search row */}
          <div className="md:hidden">
            <form onSubmit={handleSearch}>
              <SearchAutocomplete
                placeholder="Buscar productos..."
                onSearch={(q) => {
                  setSearchQuery(q);
                  setSearchParams({ ...Object.fromEntries(searchParams), q });
                }}
              />
            </form>
          </div>

          {/* Filters + Sort + View toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                activeFiltersCount > 0
                  ? 'text-white'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
              style={activeFiltersCount > 0 ? { backgroundColor: COLORS.orange, borderColor: COLORS.orange } : {}}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Filtros</span>
              {activeFiltersCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-white text-xs flex items-center justify-center font-bold" style={{ color: COLORS.orange }}>
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Inline sort dropdown */}
            <div className="hidden sm:flex items-center gap-1.5 text-sm">
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={filters.sort_by}
                onChange={(e) => {
                  const newFilters = {...filters, sort_by: e.target.value};
                  setFilters(newFilters);
                  const newParams = new URLSearchParams(searchParams);
                  if (e.target.value !== 'relevance') newParams.set('sort', e.target.value);
                  else newParams.delete('sort');
                  setSearchParams(newParams);
                }}
                className="bg-transparent border-none text-sm text-slate-600 font-medium focus:outline-none cursor-pointer pr-1"
              >
                <option value="relevance">Relevancia</option>
                <option value="price_asc">Menor precio</option>
                <option value="price_desc">Mayor precio</option>
                <option value="distance">Distancia</option>
                <option value="rating">Mejor calificados</option>
              </select>
            </div>

            <div className="flex border rounded-full overflow-hidden ml-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${viewMode === 'grid' ? 'text-white' : 'hover:bg-slate-50'}`}
                style={viewMode === 'grid' ? { backgroundColor: COLORS.navyBlue } : {}}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 transition-colors ${viewMode === 'map' ? 'text-white' : 'hover:bg-slate-50'}`}
                style={viewMode === 'map' ? { backgroundColor: COLORS.navyBlue } : {}}
              >
                <MapIcon className="w-4 h-4" />
              </button>
            </div>

            <span className="text-sm text-slate-500 whitespace-nowrap">
              {loading ? 'Buscando...' : `${totalResults} resultados`}
            </span>
          </div>

          {/* Master Category Chips - inside sticky bar */}
          {masterCategories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => {
                  const newFilters = {...filters, master_category_id: ''};
                  setFilters(newFilters);
                  setCurrentPage(1);
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('master_category');
                  setSearchParams(newParams);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  !filters.master_category_id
                    ? 'text-white shadow-sm'
                    : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
                style={!filters.master_category_id ? { backgroundColor: COLORS.navyBlue } : {}}
              >
                Todos
              </button>
              {masterCategories.map((cat) => {
                const isActive = filters.master_category_id === cat.id;
                const imgUrl = cat.thumbnail_url || cat.image_url;
                const imgSrc = imgUrl ? (imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`) : null;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      const newFilters = {...filters, master_category_id: cat.id};
                      setFilters(newFilters);
                      setCurrentPage(1);
                      const newParams = new URLSearchParams(searchParams);
                      newParams.set('master_category', cat.id);
                      setSearchParams(newParams);
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'text-white shadow-sm'
                        : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                    style={isActive ? { backgroundColor: COLORS.navyBlue } : {}}
                  >
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={cat.name}
                        className={`w-5 h-5 rounded-full object-cover ${isActive ? 'ring-1 ring-white/50' : ''}`}
                      />
                    ) : (
                      <span className="text-sm">{cat.icon}</span>
                    )}
                    {cat.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Filters Sidebar */}
      {showFilters && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="relative ml-auto w-full max-w-md bg-white h-full overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Filtros</h2>
              <button onClick={() => setShowFilters(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Categoría</label>
                <select
                  value={filters.category_id}
                  onChange={(e) => setFilters({...filters, category_id: e.target.value})}
                  className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Delivery Options */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Opciones de entrega</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.delivery_enabled}
                      onChange={(e) => setFilters({...filters, delivery_enabled: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded accent-orange-500"
                    />
                    <Truck className="w-4 h-4 text-slate-500" />
                    <span>Entrega a domicilio</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.pickup_enabled}
                      onChange={(e) => setFilters({...filters, pickup_enabled: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded accent-orange-500"
                    />
                    <Store className="w-4 h-4 text-slate-500" />
                    <span>Recoger en tienda</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.shipping_enabled}
                      onChange={(e) => setFilters({...filters, shipping_enabled: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded accent-orange-500"
                    />
                    <Package className="w-4 h-4 text-slate-500" />
                    <span>Envío por paquetería</span>
                  </label>
                </div>
              </div>

              {/* Open Now Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Disponibilidad</label>
                <label className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg cursor-pointer border border-orange-200">
                  <input
                    type="checkbox"
                    checked={filters.open_now}
                    onChange={(e) => setFilters({...filters, open_now: e.target.checked})}
                    className="w-5 h-5 text-orange-500 rounded accent-orange-500"
                  />
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="font-medium text-slate-800">Solo tiendas abiertas ahora</span>
                  </div>
                </label>
              </div>

              {/* Business Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de tienda</label>
                <select
                  value={filters.business_type}
                  onChange={(e) => setFilters({...filters, business_type: e.target.value})}
                  className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                >
                  <option value="">Todos</option>
                  {businessTypes.length > 0 ? (
                    businessTypes.map((bt) => (
                      <option key={bt.id} value={bt.id}>
                        {bt.icon ? `${bt.icon} ` : ''}{bt.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="physical_store">Tienda Física</option>
                      <option value="darkstore">DarkStore</option>
                    </>
                  )}
                </select>
              </div>

              {/* More Filters */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Más opciones</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.wholesale_enabled}
                      onChange={(e) => setFilters({...filters, wholesale_enabled: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded accent-orange-500"
                    />
                    <span>Solo mayoreo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.verified_only}
                      onChange={(e) => setFilters({...filters, verified_only: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded accent-orange-500"
                    />
                    <Shield className="w-4 h-4 text-orange-500" />
                    <span>Solo verificados</span>
                  </label>
                </div>
              </div>

              {/* Product Condition */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estado del producto</label>
                <select
                  value={filters.condition}
                  onChange={(e) => setFilters({...filters, condition: e.target.value})}
                  className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                >
                  <option value="">Todos</option>
                  <option value="new">Nuevo</option>
                  <option value="used">Usado</option>
                  <option value="refurbished">Reacondicionado</option>
                </select>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Rango de precio</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Mín"
                    value={filters.min_price}
                    onChange={(e) => setFilters({...filters, min_price: e.target.value})}
                    className="flex-1 p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                  />
                  <input
                    type="number"
                    placeholder="Máx"
                    value={filters.max_price}
                    onChange={(e) => setFilters({...filters, max_price: e.target.value})}
                    className="flex-1 p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                  />
                </div>
              </div>

              {/* Distance */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Distancia máxima: {filters.radius_km === '0' ? 'Sin límite' : `${filters.radius_km} km`}
                </label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="10"
                  value={filters.radius_km}
                  onChange={(e) => setFilters({...filters, radius_km: e.target.value})}
                  className="w-full accent-orange-500"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>Sin límite</span>
                  <span>200 km</span>
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ordenar por</label>
                <select
                  value={filters.sort_by}
                  onChange={(e) => setFilters({...filters, sort_by: e.target.value})}
                  className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                >
                  <option value="relevance">Relevancia</option>
                  <option value="price_asc">Precio: menor a mayor</option>
                  <option value="price_desc">Precio: mayor a menor</option>
                  <option value="distance">Distancia</option>
                  <option value="rating">Mejor calificados</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="sticky bottom-0 bg-white border-t p-4 flex gap-2">
              <button
                onClick={clearFilters}
                className="flex-1 py-2.5 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                Limpiar
              </button>
              <button
                onClick={applyFilters}
                className="flex-1 py-2.5 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: COLORS.navyBlue }}
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 flex-1">
        {/* View Content */}
        {viewMode === 'grid' ? (
          /* Grid View */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {loading ? (
              // Skeleton loading
              [...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-slate-200" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-slate-200 rounded" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))
            ) : results.length === 0 ? (
              <div className="col-span-full bg-white rounded-2xl border border-slate-200 text-center py-16 px-6">
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5">
                  <Search className="w-9 h-9 text-slate-300" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700">No se encontraron productos</h3>
                <p className="text-slate-500 mt-2 max-w-sm mx-auto">Intenta con otros términos de búsqueda o ajusta los filtros</p>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={() => { clearFilters(); applyFilters(); }}
                    className="mt-4 px-5 py-2 text-sm font-medium rounded-lg text-white transition-colors"
                    style={{ backgroundColor: COLORS.navyBlue }}
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            ) : (
              results.map((item) => {
                const compareAt = item.product.compare_at_price;
                const price = item.product.price || 0;
                const hasDiscount = compareAt && compareAt > price;
                const discountPct = hasDiscount ? Math.round((1 - price / compareAt) * 100) : 0;

                return (
                  <Link
                    key={`${item.product.id}-${item.branch.id}`}
                    to={`/producto/${item.product.id}`}
                    className="bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all group"
                  >
                    <div className="aspect-square bg-slate-50 relative overflow-hidden">
                      <img
                        src={getProductImage(item.product.thumbnail_url || item.product.image_url)}
                        alt={item.product.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => { e.target.src = getProductImage(); }}
                      />
                      {/* Favorite button */}
                      <button
                        className={`absolute top-2 right-2 p-2 bg-white rounded-full shadow-md transition-all hover:scale-110 ${favorites.has(item.product.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                        onClick={(e) => toggleFavorite(e, item.product.id, item.store.id, item.branch.id)}
                      >
                        <Heart className={`w-4 h-4 ${favorites.has(item.product.id) ? 'text-red-500 fill-red-500' : 'text-slate-400'}`} />
                      </button>
                      {/* Discount badge */}
                      {hasDiscount && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-red-500 text-white rounded-lg text-xs font-bold shadow-md">
                          -{discountPct}%
                        </div>
                      )}
                      {/* Out of delivery range badge */}
                      {!hasDiscount && item.branch.in_delivery_range === false && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-amber-500 text-white rounded-lg text-xs font-semibold shadow-md flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Fuera de zona
                        </div>
                      )}
                      {/* Open/Closed Badge */}
                      {(() => {
                        const status = getOpenStatusMessage(item.branch);
                        return status && (
                          <div className={`absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                            status.open
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${status.open ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            {status.msg}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-slate-800 line-clamp-2 text-sm">{item.product.name}</h3>
                      {/* Price with discount */}
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-lg font-bold" style={{ color: COLORS.navyBlue }}>
                          ${price.toFixed(2)}
                        </span>
                        {hasDiscount && (
                          <span className="text-sm text-slate-400 line-through">
                            ${compareAt.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
                        <Store className="w-3 h-3 flex-shrink-0" />
                        <VerifiedStoreName
                          name={item.store.name}
                          verified={item.store.verified}
                          size="xs"
                          nameClassName="truncate"
                        />
                      </div>
                      {item.branch.distance_km !== undefined && item.branch.distance_km !== null ? (
                        <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{item.branch.distance_km} km</span>
                          {item.branch.in_delivery_range === false && (
                            <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">
                              Solo recoger
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs text-slate-300 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>Ubicación no disponible</span>
                        </div>
                      )}
                      {/* Delivery badges + rating */}
                      <div className="flex items-center gap-2 mt-2">
                        {item.store.delivery_enabled && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px] font-medium">
                            <Truck className="w-3 h-3" />
                            Envío
                          </span>
                        )}
                        {item.store.pickup_enabled && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded text-[10px] font-medium">
                            <Store className="w-3 h-3" />
                            Recoge
                          </span>
                        )}
                        {item.store.rating > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-slate-600 ml-auto">
                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            {item.store.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        ) : (
          /* Map View */
          <div className="h-[calc(100vh-200px)] rounded-xl overflow-hidden border">
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <MapCenterUpdater center={mapCenter} />
              
              {mapMarkers.map((marker) => (
                <Marker 
                  key={marker.id} 
                  position={marker.position}
                  icon={storeIcon}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <h3 className="font-semibold">{marker.product.name}</h3>
                      <p className="font-bold" style={{ color: COLORS.navyBlue }}>${(marker.product.price || 0).toFixed(2)}</p>
                      <p className="text-sm text-slate-500">{marker.store.name}</p>
                      <p className="text-xs text-slate-400">{marker.branch.address}</p>
                      <Link 
                        to={`/producto/${marker.product.id}`}
                        className="mt-2 block text-center py-1.5 rounded text-white text-sm"
                        style={{ backgroundColor: COLORS.navyBlue }}
                      >
                        Ver producto
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* User location marker */}
              {userLocation && (
                <Marker position={[userLocation.lat, userLocation.lng]}>
                  <Popup>Tu ubicación</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && viewMode === 'grid' && (
          <div className="flex items-center justify-center gap-1.5 mt-8">
            <button
              onClick={() => { setCurrentPage(Math.max(1, currentPage - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>
            {(() => {
              let pages = [];
              const maxVisible = 5;
              let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible - 1);
              if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
              if (start > 1) {
                pages.push(1);
                if (start > 2) pages.push('...');
              }
              for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) pages.push(i);
              }
              if (end < totalPages) {
                if (end < totalPages - 1) pages.push('...');
                pages.push(totalPages);
              }
              return pages.map((p, idx) =>
                p === '...' ? (
                  <span key={`dots-${idx}`} className="px-2 text-slate-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === p
                        ? 'text-white shadow-sm'
                        : 'bg-white border border-slate-200 hover:bg-slate-50'
                    }`}
                    style={currentPage === p ? { backgroundColor: COLORS.navyBlue } : {}}
                  >
                    {p}
                  </button>
                )
              );
            })()}
            <button
              onClick={() => { setCurrentPage(Math.min(totalPages, currentPage + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <MarketplaceFooter />

      {/* Floating Cart Button */}
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};

export default MarketplaceSearch;
